import { Inject, Injectable } from '@angular/core';

import { NumberFormatterService } from '../../services/number/number-formatter.service';
import { NumberService } from '../../services/number/number.service';
import { NumberPipe } from '../../pipes/number.pipe';
import { LocaleService } from '../../services/locale/locale.service';
import { NumberParserService } from '../../services/number/number-parser.service';
import { CurrencyPipe } from '../../pipes/currency.pipe';

import type { CurrencyPipeParameters } from '../../pipes/currency.pipe';
import type { LocaleDetails } from '../../services/locale/locale.domain';
import type { FormatParameters } from './input.domain';

/**
 * TODO move all number format logic from number and currency pipes here to NumberFormatterService service
 *
 */
@Injectable({
  providedIn: 'root'
})
export class NumberInputService {
  private readonly groupSeparatorFilter: RegExp;
  private readonly decimalSeparatorFilter: RegExp;
  private readonly localeDetails: LocaleDetails;
  private readonly browserLocaleDecimalSeparator: string;

  constructor(@Inject(NumberPipe) private numberPipe: NumberPipe,
              @Inject(CurrencyPipe) private currencyPipe: CurrencyPipe,
              @Inject(NumberParserService) private numberParserService: NumberParserService,
              @Inject(LocaleService) localeService: LocaleService) {
    this.localeDetails = localeService.getLocaleDetails();
    this.groupSeparatorFilter = new RegExp('\\' + this.localeDetails.groupSeparator, 'g');
    this.decimalSeparatorFilter = new RegExp('\\' + this.localeDetails.decimalSeparator, 'g');
    this.browserLocaleDecimalSeparator = this.getBrowserLocaleDecimalSeparator();
  }

  format(value: number, formatParameters: FormatParameters): string {
    let formattedValue: string;

    if (this.isCurrencyFormat(formatParameters.format)) {
      formattedValue = this.currencyPipe.transform(value, formatParameters as CurrencyPipeParameters);
    } else {
      formattedValue = this.numberPipe.transform(value, formatParameters);
    }
    if (formatParameters.noGroupSeparator || formatParameters.editFormat) {
      formattedValue = formattedValue.replace(this.groupSeparatorFilter, '');
    }
    if (formatParameters.editFormat) {
      // replace decimal separator with one from current browser locale
      // int order for the value to be accepted by inputs with type=number
      formattedValue = formattedValue.replace(this.decimalSeparatorFilter, this.browserLocaleDecimalSeparator);
    }
    return formattedValue;
  }

  parse(value: string, format: string, decimalTruncate?: boolean): number {
    const result = this.numberParserService.parse(value);
    const formatPrecision = NumberFormatterService.parsePrecision(result, format);
    const precision = NumberFormatterService.getAdjustedPrecision(result, format, formatPrecision);

    if(decimalTruncate) {
      return NumberService.truncateDecimal(result, formatPrecision);
    }

    return NumberService.round(result, precision);
  }

  isCurrencyFormat(format: string): boolean {
    return format !== undefined && format.startsWith('c');
  }

  isDecimalTruncate(format: string): boolean {
    return format !== undefined && format.startsWith('t');
  }

  // See WBR-9614, in French browser if this api returns ',', it will break number-input directive to convert string number value,
  // such as '234,56', to a number when switch input.type to "number", so hard code the return value to '.' always.
  private getBrowserLocaleDecimalSeparator(): string {
    //return 0.1.toLocaleString(this.localeDetails.locale).replace(/\d/g, '');
    return '.';
  }
}
